import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { auth as authApi } from "@/lib/api";
import { toast } from "sonner";
import { Check, AlertTriangle } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";
import PasswordRequirements, { isPasswordValid } from "@/components/PasswordRequirements";
import useAppReady from "@/lib/useAppReady";

const LOGO = "/assets/images/kiddo-logo.png";
const WORDMARK = "/assets/images/kiddo-wordmark.png";

export default function ResetPassword() {
  useAppReady();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isPasswordValid(pwd)) { setError("Please choose a password that meets all the requirements below."); return; }
    if (pwd !== pwd2) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await authApi.resetPassword(token, pwd);
      setDone(true);
      toast.success("Password updated! 🌿");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-soft-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6">
          <img src={LOGO} alt="" className="w-12 h-12 rounded-[22%]" />
          <img src={WORDMARK} alt="Kiddo+" className="h-9 w-auto" />
        </Link>

        {!token ? (
          <div className="text-center" data-testid="reset-no-token">
            <AlertTriangle className="w-12 h-12 text-coral mx-auto mb-3" />
            <h1 className="font-heading text-2xl mb-2">Missing reset token</h1>
            <p className="text-inkmid text-sm mb-6">Please use the link from your reset email, or request a new one.</p>
            <Link to="/login"><Button className="bg-coral hover:bg-coral-dark text-white rounded-full font-bold px-8">Back to login</Button></Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-mint/15 flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-mint" />
            </div>
            <h1 className="font-heading text-2xl mb-2">Password updated 🌿</h1>
            <p className="text-inkmid text-sm mb-6">You can now sign in with your new password.</p>
            <Button onClick={()=>navigate("/login")} className="bg-coral hover:bg-coral-dark text-white rounded-full font-bold px-8" data-testid="reset-to-login-btn">Go to login</Button>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl text-center mb-2">Choose a new password</h1>
            <p className="text-inkmid text-center text-sm mb-6">Pick something strong and easy for you to remember.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="np">New password</Label>
                <PasswordInput id="np" value={pwd} onChange={(e)=>setPwd(e.target.value)} required minLength={8}
                  autoComplete="new-password" testIdSuffix="reset-new"
                  className="rounded-2xl border-plum/10 mt-1 py-6" data-testid="reset-new-password" />
                <div className="mt-2">
                  <PasswordRequirements value={pwd} variant="tooltip" />
                </div>
              </div>
              <div>
                <Label htmlFor="np2">Confirm password</Label>
                <PasswordInput id="np2" value={pwd2} onChange={(e)=>setPwd2(e.target.value)} required minLength={8}
                  autoComplete="new-password" testIdSuffix="reset-confirm"
                  className="rounded-2xl border-plum/10 mt-1 py-6" data-testid="reset-confirm-password" />
              </div>
              {error && <p className="text-sm text-[#E24B4A] font-semibold">{error}</p>}
              <Button type="submit" disabled={loading || !isPasswordValid(pwd) || pwd !== pwd2} className="w-full bg-coral hover:bg-coral-dark text-white rounded-full py-6 font-bold disabled:opacity-50" data-testid="reset-submit-btn">
                {loading ? "Updating…" : "Update password"}
              </Button>
            </form>
            <p className="text-center text-sm text-inkmid mt-6">
              <Link to="/login" className="text-coral font-semibold">Back to login</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
